import {memo, useEffect} from 'react';
import {Trans, useTranslation} from 'react-i18next';
import {Controller, useFormContext} from 'react-hook-form';
import {EXTRA_FORM_NAMES, FORM_NAMES, FormTypes} from '../../utils/guestForm';
import {Contract} from '@guestapp/sdk';
import {useErrorModal} from '../../hooks';
import {useSummary} from 'context/summary';
import {useSchema, type AddFormType, type AddFormV2Type} from '../../context/guest';
import {getRegExpFromString} from '@guestapp/core';
import {downloadFromLink} from '../../utils/common';
import {PrivacyPolicyLink} from '../PrivacyPolicyLink';
import {GuestEmailField} from '../GuestFormFields';
import {FieldsGroup} from 'components/FieldsGroup';
import {
  AcceptWarning,
  CheckboxContainer,
  LinkInText,
  StyledCheckbox,
  StyledComputedBox,
  StyledLoader,
} from './styled';

const checkHasAlreadyEmail = (formFields: AddFormType | AddFormV2Type) =>
  ((formFields as AddFormType)?.[FORM_NAMES.email]?.active &&
    (formFields as AddFormType)?.[FORM_NAMES.email]?.required) ||
  ((formFields as AddFormV2Type)?.[FORM_NAMES.email]?.is_active &&
    (formFields as AddFormV2Type)?.[FORM_NAMES.email]?.is_required);

type ContractBoxProps = {
  contracts?: Contract[];
  hasError?: boolean;
  isLoading?: boolean;
  defaultEmail?: string;
  isAutomatedSignature?: boolean;
};
const ContractBox = memo(
  ({
    contracts,
    hasError,
    isLoading,
    defaultEmail,
    isAutomatedSignature,
  }: ContractBoxProps) => {
    const {t} = useTranslation();
    const {ErrorModal: ErrorModalContract, displayError} = useErrorModal();
    const {data: summary} = useSummary();
    const {control, watch, resetField, getValues} = useFormContext<FormTypes>();
    const isContractEnabled = summary?.is_contract_enabled;
    const {formFields} = useSchema();
    const hasAlreadyEmail = checkHasAlreadyEmail(formFields);

    const shouldSendCopyContractToEmail = watch(
      EXTRA_FORM_NAMES.send_copy_contract_email,
    );
    const needToShowAdditionalEmailField =
      !defaultEmail && !hasAlreadyEmail && shouldSendCopyContractToEmail;

    useEffect(
      function resetInactiveContractEmailField() {
        const value = getValues()[FORM_NAMES.contract_email];
        if (!needToShowAdditionalEmailField && value) {
          resetField(FORM_NAMES.contract_email);
        }
      },
      [getValues, needToShowAdditionalEmailField, resetField],
    );

    const viewContract = () => {
      const contract = contracts?.[0];

      if (hasError || !contract?.file) {
        displayError();
        return;
      }

      downloadFromLink(contract?.file);
    };

    return (
      <>
        {isLoading && <StyledLoader width={60} />}
        {isContractEnabled && !isLoading && (
          <FieldsGroup title={t('contract')} centered={false}>
            <CheckboxContainer className="sign-block__checkbox-container">
              <Controller
                name={EXTRA_FORM_NAMES.send_copy_contract_email}
                control={control}
                render={({field, fieldState: {error}}) => (
                  <StyledCheckbox
                    label={<Trans i18nKey="send_copy_contract_email" />}
                    error={error?.message}
                    className="sign-block__checkbox"
                    {...field}
                  />
                )}
              />
              <StyledComputedBox expanded={needToShowAdditionalEmailField}>
                <GuestEmailField
                  name={FORM_NAMES.contract_email}
                  required={t('required')}
                  label={t('email')}
                  format={getRegExpFromString(
                    formFields?.[FORM_NAMES.email]?.format || '',
                  )}
                />
              </StyledComputedBox>
            </CheckboxContainer>
          </FieldsGroup>
        )}
        <AcceptWarning className="sign-block__warning">
          {/* {!showSignArea && <>{t('by_clicking_next_you_accept_the')} </>} */}
          {!isContractEnabled && !isAutomatedSignature && (
            <Trans i18nKey="by_clicking_next_you_accept_the">
              By clicking <b>‘Sign form’</b> you accept to sign the form with your name
              and the Privacy Policy
            </Trans>
          )}
          {!isContractEnabled && isAutomatedSignature && (
            <Trans i18nKey="by_clicking_signt_now_you_accept_the">
              By clicking <b>‘Sign now’ </b>you accept to sign the form with your name,
              the short term
            </Trans>
          )}
          {isContractEnabled && (
            <>
              {!isAutomatedSignature ? (
                <Trans i18nKey="by_clicking_next_you_accept_the">
                  By clicking <b>‘Sign form’</b> you accept to sign the form with your
                  name and the
                </Trans>
              ) : (
                <Trans i18nKey="by_clicking_signt_now_you_accept_the">
                  By clicking <b>‘Sign now’ </b>you accept to sign the form with your
                  name, the short term
                </Trans>
              )}{' '}
              <LinkInText data-testid="contract-btn" onClick={viewContract}>
                {t('contract')}
              </LinkInText>
            </>
          )}
          {isContractEnabled && ` ${t('and_you_accept_our')} `}
          <PrivacyPolicyLink />
        </AcceptWarning>
        <ErrorModalContract
          title={t('contract_not_available')}
          message={t('contract_not_available_description')}
        />
      </>
    );
  },
);

export {ContractBox};
